import React, {
  useMemo,
  useState,
  useCallback,
  Fragment,
  useEffect,
  useRef,
} from 'react';
import {Combobox, Transition} from '@headlessui/react';
import countryList from 'react-select-country-list';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import {search} from '../../assets/images/images';

/**
 * A searchable country selection component with multi-select and "select all" functionality
 * @param {Object} props
 * @param {Function} props.onChange - Callback function when selection changes
 * @param {(string[]|string)} [props.countryData] - Initial country selection
 * @param {string} [props.className] - Additional CSS classes
 */
const CountryCombobox = ({
  onChange,
  countryData,
  className = '',
  isLoading,
}) => {
  // Initialize countries list once
  const countries = useMemo(() => countryList().getData(), []);

  const initialRef = useRef(true);

  // Initialize selected countries based on countryData prop
  const getInitialSelection = useCallback(() => {
    if (typeof countryData === 'string') {
      const country = countries.find(c => c.value === countryData);
      return country ? [country] : [];
    }
    if (!isLoading && countryData?.length === 0) return countries;
    return countries.filter(c => countryData?.includes(c.value));
  }, [countryData, isLoading, countries]);

  const selectedCountries = useMemo(
    () => !isLoading && getInitialSelection(),
    [getInitialSelection, isLoading],
  );
  const [selected, setSelected] = useState([]);

  const isAllSelected = useMemo(
    () => selected?.length === countries?.length,
    [selected, countries],
  );

  // State management
  const [query, setQuery] = useState('');
  const [selectAll, setSelectAll] = useState(isAllSelected);

  useEffect(() => {
    if (!isLoading && initialRef.current) {
      setTimeout(() => {
        setSelected(selectedCountries);
        initialRef.current = false;
      }, 1000);
    }
  }, [selectedCountries, setSelected, isLoading]);

  // Memoized filtered countries based on search query
  const filteredCountries = useMemo(() => {
    if (!query) return countries;
    const normalizedQuery = query.toLowerCase().replace(/\s+/g, '');
    return countries.filter(country =>
      country.label.toLowerCase().replace(/\s+/g, '').includes(normalizedQuery),
    );
  }, [query, countries]);

  // Handlers
  const handleSelectAll = useCallback(() => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newSelection = newSelectAll ? countries : [];
    setSelected(newSelection);
    onChange(newSelection);
  }, [countries, onChange, selectAll]);

  const handleSelectionChange = useCallback(
    newSelection => {
      setSelected(newSelection);
      setSelectAll(newSelection.length === countries.length);
      onChange(newSelection);
    },
    [countries.length, onChange],
  );

  // Display value formatter
  const getDisplayValue = useCallback(selection => {
    if (!selection?.length) return '';
    if (selection?.length === 1) return selection[0].label;
    if (selection?.length === 2) return selection.map(s => s.label).join(', ');
    return `${selection.length} countries selected`;
  }, []);

  return (
    <div className={`relative w-full ${className}`}>
      <Combobox value={selected} onChange={handleSelectionChange} multiple>
        <div className="relative">
          <Combobox.Input
            className="w-full h-12 px-4 py-2 text-sm border-1 border-grey60 rounded focus:outline-none focus:ring-0 "
            displayValue={() => getDisplayValue(selected)}
            onChange={e => setQuery(e.target.value)}
            placeholder="Select countries..."
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-4">
            <img
              src={search}
              alt=""
              width={16}
              height={16}
              title="Toggle dropdown"
            />
          </Combobox.Button>
        </div>

        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
          as={Fragment}
        >
          <Combobox.Options
            // className="absolute z-10 w-full mt-2 overflow-auto bg-white rounded-lg shadow-lg max-h-64 ring-1 ring-black ring-opacity-5 focus:outline-none"
            className="absolute mt-2 z-50 max-h-64 w-full overflow-auto rounded bg-white border border-grey60 p-2 text-sm"
          >
            <div className="p-2">
              {/* Select All Option */}
              <div
                className={`flex items-center gap-2 p-1 cursor-pointer ${
                  selectAll || isAllSelected
                    ? 'bg-brandBlue text-white'
                    : 'text-grey'
                }`}
                onClick={handleSelectAll}
              >
                <input
                  type="checkbox"
                  checked={selectAll || isAllSelected}
                  readOnly
                  className={`w-3 h-3  ${
                    selectAll || isAllSelected ? 'border-1 border-white' : ''
                  }`}
                />
                <span className="font-medium">Select all countries</span>
              </div>

              <div className="h-[1px] w-full bg-grey5 my-2" />

              {/* Country List */}
              {filteredCountries?.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No countries found
                </div>
              ) : (
                filteredCountries?.map(country => (
                  <Combobox.Option
                    key={country.value}
                    value={country}
                    className={({active, selected}) =>
                      `flex items-center gap-2 px-1 cursor-pointer text-grey ${
                        active ? 'bg-grey5 text-grey' : ''
                      } ${
                        selected ? 'bg-brandBlue text-white mb-1' : 'text-grey'
                      }`
                    }
                  >
                    {({selected}) => (
                      <>
                        <input
                          type="checkbox"
                          checked={selected}
                          readOnly
                          className="w-3 h-3 rounded"
                        />
                        <span className="flex items-center gap-2">
                          <span className="text-lg">
                            {getUnicodeFlagIcon(country.value)}
                          </span>
                          <span className="text-sm">{country.label}</span>
                        </span>
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </div>
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
};

export default CountryCombobox;
